import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils.ts";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: cn(
          "bg-[#0055CC] hover:bg-[#001D6C] active:bg-[#001141] text-semibold text-[white]",
          "disabled:bg-transparent disabled:text-[#B3B9C4]",
        ),
        primaryFilled: cn(
          "bg-[#0055CC] hover:bg-[#001D6C] active:bg-[#001141] text-semibold text-[white]",
          "disabled:bg-transparent disabled:text-[#B3B9C4]",
        ),
        primaryOutlined: cn(
          "custom-button-primary-outlined",
          "active:bg-[#002D9C] active:text-[white] bg-transparent text-semibold text-[#0055CC]",
          "disabled:bg-transparent disabled:text-[#B3B9C4]",
        ),
        secondaryFilled: cn(
          "bg-[#F1F2F4] hover:bg-[#E0E0E0] active:bg-[#C6C6C6] text-[#44546F]",
          "disabled:bg-[white] disabled:text-[#B3B9C4]",
        ),
        secondaryOutlined:
          "custom-button-secondary-outlined hover:bg-[#E0E0E0] active:bg-[#C6C6C6]",
        dangerFilled: cn(
          "bg-[#750E13] hover:bg-[#520408] active:bg-[#2D0709] text-semibold text-[white]",
          "disabled:bg-transparent disabled:text-[#B3B9C4]",
        ),
        warningOutlined: cn(
          "bg-[transparent] hover:bg-[#FFF3EB] active:bg-[#38291E] text-semibold text-[#A54800] active:text-[white]",
          "disabled:bg-transparent disabled:text-[#B3B9C4]",
        ),
        whiteFilled: cn(
          "bg-[white] border-button-white-filled hover:bg-[#E0E0E0] active:bg-[#C6C6C6] text-[#44546F]",
          "disabled:bg-[white] disabled:text-[#B3B9C4]",
        ),
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        md: "h-[40px] sm:h-[58px] px-4 py-2 has-[>svg]:px-3",
        lg: "h-[48px] sm:h-[58px] px-4 py-2 has-[>svg]:px-3",
        icon: "size-9",
        iconRounded: "h-[32px] w-[32px] rounded-[44px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
